// The wire + stored shape of everything log-pile ingests. The same types are
// used by the client SDK (client/logPile.ts), the ingest Lambdas and the
// website, so a field added here shows up end to end.

export const LEVELS = ["debug", "info", "warning", "error", "fatal"] as const;
export type Level = (typeof LEVELS)[number];

export const CAPTURE_KINDS = ["error", "message", "event"] as const;
export type CaptureKind = (typeof CAPTURE_KINDS)[number];

export const RUNTIMES = ["lambda", "browser", "node", "other"] as const;
export type Runtime = (typeof RUNTIMES)[number];

/**
 * One step in the lead-up to a capture. Timestamped so the website can lay
 * them out as a timeline; `seq` is the client's monotonic counter within a
 * flow, which orders crumbs that share a millisecond and lets the server
 * de-duplicate when a capture and a later amendment overlap.
 */
export type Breadcrumb = {
  ts: string; // ISO 8601, client clock
  seq: number;
  message: string;
  category?: string; // e.g. "http", "db", "ui.click", "validation"
  level?: Level;
  data?: Record<string, unknown>;
};

export type CaptureUser = {
  id?: string;
  username?: string;
  email?: string;
};

export type CaptureError = {
  name: string;
  message: string;
  stack?: string;
};

/** What a client sends. Everything the server derives lives on StoredCapture. */
export type CaptureInput = {
  id: string; // client-generated uuid, so amendments can reference it before the server has seen it
  kind: CaptureKind;
  level: Level;
  message: string;
  timestamp: string; // ISO 8601, when the client captured it

  // Where it came from. `source` is the service/app name and is the main
  // thing the website filters on. For the beacon endpoint the server
  // overwrites `source` with the label its key vouches for. `env` is the
  // *sender's* environment ("dev" / "prod" / "local"…), required because it
  // is part of the list-view key (see table.ts) and of the fingerprint.
  source: string;
  runtime: Runtime;
  env: string;
  release?: string; // app version
  lambdaName?: string;
  location?: string; // URL in a browser, handler/module name on a server
  requestId?: string; // awsRequestId, or a browser session id
  sdkVersion?: string; // LOG_PILE_CLIENT_VERSION of the copied-in client file that sent this

  // A flow groups breadcrumbs: one Lambda invocation, one page view. Every
  // capture in the same flow carries the same breadcrumbs-so-far.
  flowId?: string;

  user?: CaptureUser;
  error?: CaptureError;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;

  // Explicit grouping key. If absent the server computes one (see
  // backend/utils/fingerprint.ts).
  fingerprint?: string;

  breadcrumbs: Breadcrumb[];
  // Set by the client when it could not fit every breadcrumb in this
  // message; the rest follow as AmendInput messages carrying the same id.
  breadcrumbsTruncated?: boolean;
  breadcrumbsOmitted?: number;
  // Set by the client's rate limiter on the first capture it lets through
  // after suppressing some: how many were logged to the console instead of
  // sent since the previous sent capture.
  suppressedBefore?: number;
};

/**
 * Extra breadcrumbs for an already-sent capture. Stored as its own item under
 * the capture's partition, so it can land before, after, or more than once
 * relative to the capture itself (EventBridge is at-least-once and unordered)
 * without clobbering anything. `chunk` numbers the amendments from one client
 * so a replay overwrites the same item rather than appending twice.
 */
export type AmendInput = {
  captureId: string;
  chunk: number; // 1-based, per capture
  breadcrumbs: Breadcrumb[];
};

/** The beacon/HTTP ingest body: one or more messages under one public key. */
export type IngestEnvelope = {
  key: string;
  items: IngestMessage[];
};

export type IngestMessage =
  | { type: "capture"; data: CaptureInput }
  | { type: "amend"; data: AmendInput };

// EventBridge envelope on the shared bus. `Source` is the sending service's
// name (e.g. "todoit-site-backend") — that's for AWS-side tracking/metrics
// and rules; log-pile itself reads the service from `detail.source`, the same
// field the beacon path fills in, and matches only on detail-type. The
// detail-type prefix names the *listener* ("logpile.*"), so other listeners'
// events can share the bus unambiguously. Captures split by kind so a future
// rule can subscribe to just errors.
export const BUS_DETAIL_TYPE = {
  captureError: "logpile.captureError",
  captureMessage: "logpile.captureMessage",
  captureEvent: "logpile.captureEvent",
  amend: "logpile.amend",
} as const;
export type BusDetailType = (typeof BUS_DETAIL_TYPE)[keyof typeof BUS_DETAIL_TYPE];

// The event's `detail`: the payload under `data`, with room beside it for
// envelope-level metadata that isn't part of the capture itself.
export type BusDetail<T = CaptureInput | AmendInput> = {
  data: T;
  meta?: { sdkVersion?: string; sentAt?: string };
};

export const detailTypeForKind = (kind: CaptureKind): BusDetailType =>
  kind === "error" ? BUS_DETAIL_TYPE.captureError : kind === "message" ? BUS_DETAIL_TYPE.captureMessage : BUS_DETAIL_TYPE.captureEvent;
export const kindForDetailType = (detailType: string): CaptureKind | undefined =>
  detailType === BUS_DETAIL_TYPE.captureError
    ? "error"
    : detailType === BUS_DETAIL_TYPE.captureMessage
      ? "message"
      : detailType === BUS_DETAIL_TYPE.captureEvent
        ? "event"
        : undefined;

export const CAPTURE_STATUSES = ["open", "resolved", "ignored"] as const;
export type CaptureStatus = (typeof CAPTURE_STATUSES)[number];

/** A capture as the API returns it (table keys stripped). */
export type StoredCapture = Omit<CaptureInput, "fingerprint"> & {
  fingerprint: string;
  groupId: string;
  status: CaptureStatus; // "open" on ingest; a person resolves/ignores from the site
  receivedAt: string; // server clock; orders the lists. `timestamp` is the producer's "happened at"
  // Flattened copies of nested fields the list view filters on. A GSI can't
  // key off a nested attribute and a FilterExpression on `user.id` is
  // awkward to build; one flat string each keeps the query code simple.
  userId?: string;
  amendments: number; // how many crumbs#N chunks exist for this capture
  floodSampled?: boolean; // kept as a 1-in-N sample while its group was over the flood limit
  ttl?: number; // epoch seconds, DynamoDB TTL
};

/** A capture plus every amendment folded in and sorted — the detail view. */
export type CaptureDetail = StoredCapture & {
  timeline: Breadcrumb[]; // merged + de-duplicated + sorted by (ts, seq)
};
